import React, { useMemo } from 'react';
import { Box, Chip, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { SiteScheduleService } from '../../../services/SiteScheduleService';
import {
  SiteSchedule,
  EVENT_TYPE_LABEL,
  eventTypeFallback,
  compareByScheduleTime,
  scheduleTime,
} from '../../../models/SiteSchedule';

/**
 * 사이트 별 "가장 가까운 일정" 배지 — 현장 관리 테이블에서 구동 상태 컬럼을 대체.
 *
 * - GET /v0.1/site-management/projects/:id/schedules?from=now (React Query 캐시).
 * - 미래(now 이후) 일정 중 취소/완료를 제외하고 가장 빠른 1건만 표시.
 * - 클릭 시 /project/:siteId/schedules 로 이동. row onClick 전파 막음.
 */
interface Props {
  siteId: string | number;
}

/** now 기준 상대 일자 라벨. 오늘/내일/모레/N일 후 또는 빈 문자열. */
const relativeDay = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOfDay = (t: Date) => new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const diffDays = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);
  if (diffDays <= 0) return '오늘';
  if (diffDays === 1) return '내일';
  if (diffDays === 2) return '모레';
  if (diffDays < 7) return `${diffDays}일 후`;
  return '';
};

/** 월/일 (요일) HH:mm — 정확한 일시. */
const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 미래 일정 중 취소/완료 제외, scheduled_at 오름차순.
 *
 * 시점 없는 메모(scheduled_at=null)는 여기서 빠진다 — "다음 일정" 배지는
 * 언제인지 아는 것만 보여줘야 뜻이 있다. scheduleTime 이 null 을 걸러 준다
 * (new Date(null) 은 NaN 이 아니라 epoch 라서 직접 파싱하면 조용히 통과한다).
 */
const pickUpcoming = (list: SiteSchedule[] | undefined): SiteSchedule[] => {
  if (!list || list.length === 0) return [];
  const now = Date.now();
  return list
    .filter((s) => s.status !== 'cancelled' && s.status !== 'done')
    .filter((s) => {
      const t = scheduleTime(s);
      return t !== null && t >= now;
    })
    .sort((a, b) => compareByScheduleTime(a, b, 'asc'));
};

/** 상대 일자별 긴급도 색 — 한눈에 "오늘/내일 것부터" 스캔되도록. */
const urgencyColor = (rel: string): 'error' | 'warning' | 'default' => {
  if (rel === '오늘') return 'error';
  if (rel === '내일') return 'warning';
  return 'default';
};

const NextScheduleBadge: React.FC<Props> = ({ siteId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const numericId = Number(siteId);
  const enabled = Number.isFinite(numericId) && numericId > 0;

  // from=now 만 지정 — 미래 일정 전체를 받아 가장 가까운 1건을 고른다.
  const fromIso = useMemo(() => new Date().toISOString(), []);

  const { data, isLoading } = useQuery({
    queryKey: ['site-schedules', 'next', numericId],
    queryFn: () => SiteScheduleService.list(numericId, fromIso),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!enabled) return;
    navigate(`/project/${siteId}/schedules`);
  };

  if (!enabled) return <Typography variant="caption" color="text.secondary">—</Typography>;
  if (isLoading) {
    return (
      <Typography variant="caption" color="text.secondary">
        로딩 중...
      </Typography>
    );
  }

  const upcoming = pickUpcoming(data);
  const next = upcoming[0] ?? null;

  if (!next) {
    return (
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          color: theme.palette.text.secondary,
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main },
        }}
      >
        <Typography variant="caption">예정 일정 없음</Typography>
        <ArrowForwardIcon sx={{ fontSize: 12, opacity: 0.7 }} />
      </Box>
    );
  }

  const rel = relativeDay(next.scheduled_at);
  const eventLabel = EVENT_TYPE_LABEL[eventTypeFallback(next.event_type)];
  const color = urgencyColor(rel);
  const accent =
    color === 'error'
      ? theme.palette.error.main
      : color === 'warning'
        ? theme.palette.warning.main
        : theme.palette.primary.main;
  const remainingCount = upcoming.length - 1;

  return (
    <Tooltip
      title={`${formatDateTime(next.scheduled_at)} · ${eventLabel} · ${next.title}${
        remainingCount > 0 ? ` (이 외 ${remainingCount}건)` : ''
      } — 클릭하여 일정으로 이동`}
    >
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        sx={{
          minWidth: 180,
          maxWidth: 260,
          pl: 1,
          pr: 0.75,
          py: 0.5,
          borderRadius: 1,
          borderLeft: `3px solid ${accent}`,
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          bgcolor: alpha(accent, color === 'default' ? 0.04 : 0.08),
          '&:hover': { bgcolor: alpha(accent, color === 'default' ? 0.1 : 0.16) },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(next.scheduled_at)}
          </Typography>
          {rel && (
            <Chip
              label={rel}
              size="small"
              color={color === 'default' ? 'primary' : color}
              variant={color === 'default' ? 'outlined' : 'filled'}
              sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
            />
          )}
          {remainingCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              +{remainingCount}건
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Chip
            label={eventLabel}
            size="small"
            variant="outlined"
            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
          />
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 500,
            }}
          >
            {next.title}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default NextScheduleBadge;
